import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Button,
  Space,
  Tag,
  Select,
  DatePicker,
  Typography,
  Modal,
  Form,
  Input,
  InputNumber,
  Row,
  Col,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';
import { projectKeys } from '@/hooks/useProjects';
import { projectService, type ProjectCostRow } from '@/services/project.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { CostControlLineFields } from '@/components/project/CostControlLineFields';
import { BudgetExceedanceRequestModal } from '@/components/project/BudgetExceedanceRequestModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const COST_CATEGORIES = ['Material', 'Labor', 'Subcontract', 'Overhead', 'Equipment'];

export const ProjectCostsPage: React.FC = () => {
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { id: routeProjectId } = useParams<{ id?: string }>();
  const [projectId, setProjectId] = useState<string | undefined>(routeProjectId);
  const [costCategory, setCostCategory] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([
    null,
    null,
  ]);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [exceedanceOpen, setExceedanceOpen] = useState(false);
  const [exceedanceCheck, setExceedanceCheck] = useState<Record<string, unknown> | null>(null);
  const [exceedanceLines, setExceedanceLines] = useState<Array<Record<string, unknown>>>([]);
  const [exceedanceProjectId, setExceedanceProjectId] = useState('');
  const [exceedanceCostId, setExceedanceCostId] = useState('');
  const [postingId, setPostingId] = useState<string | null>(null);

  useEffect(() => {
    if (routeProjectId) setProjectId(routeProjectId);
  }, [routeProjectId]);

  const params = {
    project_id: projectId,
    cost_category: costCategory,
    date_from: dateRange[0]?.format('YYYY-MM-DD'),
    date_to: dateRange[1]?.format('YYYY-MM-DD'),
  };

  const { data: rows = [], isLoading } = useQuery({
    queryKey: [...projectKeys.all, 'company-costs', params],
    queryFn: () => projectService.getCompanyCosts(params),
  });

  const { data: projects = [] } = useQuery({
    queryKey: projectKeys.list(),
    queryFn: () => projectService.getProjects(),
    staleTime: 60_000,
  });

  const { data: costCenters = [] } = useQuery({
    queryKey: [...projectKeys.all, 'cost-centers'],
    queryFn: () => projectService.getProjectCostCenters(),
    staleTime: 300_000,
  });

  const openExceedanceForCost = async (cost: ProjectCostRow) => {
    const pid = cost.project_id;
    const lines = [
      {
        projectId: pid,
        wbsNodeId: (cost as any).wbs_node_id || null,
        cbsCodeId: (cost as any).cbs_code_id || null,
        costCategory: cost.cost_category || 'Overhead',
        amount: Number(cost.total_cost) || 0,
        sourceLineId: cost.id,
      },
    ];
    try {
      const check = await projectService.checkCostControl(pid, {
        lines,
        excludeSourceType: 'project_cost',
        excludeSourceId: cost.id,
      });
      setExceedanceProjectId(pid);
      setExceedanceCostId(cost.id);
      setExceedanceLines(lines);
      setExceedanceCheck(check);
      setExceedanceOpen(true);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not check budget');
    }
  };

  const postCost = async (cost: ProjectCostRow) => {
    setPostingId(cost.id);
    try {
      await projectService.postProjectCost(cost.project_id, cost.id);
      message.success('Project cost posted');
      await queryClient.invalidateQueries({ queryKey: projectKeys.all });
    } catch (e: any) {
      const code = e?.response?.data?.errors?.code || e?.response?.data?.code;
      const msg = e?.response?.data?.message || e?.message || 'Failed to post';
      if (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(String(msg))) {
        message.warning(msg);
        await openExceedanceForCost(cost);
      } else {
        message.error(msg);
      }
    } finally {
      setPostingId(null);
    }
  };

  const columns: ColumnsType<ProjectCostRow> = [
    {
      title: 'Cost date',
      dataIndex: 'cost_date',
      key: 'cost_date',
      width: 120,
      render: (d: string) => (d ? dayjs(d).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Project',
      key: 'project',
      width: 200,
      render: (_, r) => (
        <Text>
          {r.project?.project_number ? `${r.project.project_number} — ` : ''}
          {r.project?.project_name ?? '—'}
        </Text>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'cost_category',
      key: 'cost_category',
      width: 120,
    },
    {
      title: 'Total',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 120,
      align: 'right',
      render: (v: number, r) => formatCurrency(Number(v), r.currency),
    },
    {
      title: 'Posted',
      dataIndex: 'is_posted',
      key: 'is_posted',
      width: 90,
      render: (p: boolean) => (
        <Tag color={p ? 'green' : 'default'}>{p ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      render: (_, r) =>
        !r.is_posted ? (
          <Button type="link" size="small" loading={postingId === r.id} onClick={() => void postCost(r)}>
            Post
          </Button>
        ) : null,
    },
  ];

  const submitCost = async () => {
    const values = await form.validateFields();
    const pid = values.project_id as string;
    setSaving(true);
    try {
      const qty = values.quantity ?? 1;
      const unit = values.unit_cost ?? values.total_cost;
      const created = await projectService.createProjectCost(pid, {
        cost_date: values.cost_date.format('YYYY-MM-DD'),
        cost_center_id: values.cost_center_id,
        cost_category: values.cost_category,
        quantity: qty,
        unit_cost: unit,
        total_cost: values.total_cost,
        currency: values.currency ?? 'AED',
        source_type: 'Manual',
        description: values.description,
        wbs_node_id: values.wbs_node_id || null,
        cbs_code_id: values.cbs_code_id || null,
      });
      try {
        await projectService.postProjectCost(pid, created.id);
        message.success('Project cost saved and posted');
        setModalOpen(false);
        form.resetFields();
      } catch (e: any) {
        const code = e?.response?.data?.errors?.code || e?.response?.data?.code;
        const msg = e?.response?.data?.message || e?.message || 'Saved but post failed';
        message.warning(msg);
        if (code === 'BUDGET_EXCEEDANCE_REQUIRED' || /exceed|budget/i.test(String(msg))) {
          setModalOpen(false);
          await openExceedanceForCost(created);
        }
      }
      await queryClient.invalidateQueries({ queryKey: projectKeys.all });
      await queryClient.invalidateQueries({ queryKey: [...projectKeys.all, 'company-costs'] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Failed to save project cost');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>Project costs</Title>
        <Text type="secondary">
          {routeProjectId
            ? 'Costs for this project (Cost Control / Health actuals)'
            : 'Posted and draft costs across projects'}
        </Text>
      </div>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          allowClear={!routeProjectId}
          disabled={!!routeProjectId}
          placeholder="Project"
          style={{ width: 260 }}
          value={projectId}
          onChange={setProjectId}
          showSearch
          optionFilterProp="children"
        >
          {projects.map((p) => (
            <Select.Option key={p.id} value={p.id}>
              {p.project_number} — {p.project_name}
            </Select.Option>
          ))}
        </Select>
        <Select
          allowClear
          placeholder="Category"
          style={{ width: 160 }}
          value={costCategory}
          onChange={setCostCategory}
        >
          {COST_CATEGORIES.map((c) => (
            <Select.Option key={c} value={c}>
              {c}
            </Select.Option>
          ))}
        </Select>
        <RangePicker value={dateRange as any} onChange={(v) => setDateRange(v || [null, null])} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)}>
          Add cost
        </Button>
      </Space>

      <Table<ProjectCostRow>
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: 1100 }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title="Add project cost"
        open={modalOpen}
        onOk={submitCost}
        onCancel={() => {
          setModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={saving}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="project_id"
            label="Project"
            rules={[{ required: true, message: 'Select project' }]}
          >
            <Select showSearch optionFilterProp="children" placeholder="Project">
              {projects.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.project_number} — {p.project_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="cost_date"
                label="Cost date"
                rules={[{ required: true, message: 'Required' }]}
                initialValue={dayjs()}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="cost_center_id"
                label="Cost center"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="children"
                  placeholder={costCenters.length ? 'Cost center' : 'No cost centers — create in admin'}
                  notFoundContent={costCenters.length ? undefined : 'No cost centers'}
                >
                  {costCenters.map((cc) => (
                    <Select.Option key={cc.id} value={cc.id}>
                      {cc.cost_center_code} — {cc.cost_center_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="cost_category"
            label="Category"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select>
              {COST_CATEGORIES.map((c) => (
                <Select.Option key={c} value={c}>
                  {c}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev.project_id !== cur.project_id ||
              prev.cost_category !== cur.cost_category ||
              prev.wbs_node_id !== cur.wbs_node_id ||
              prev.cbs_code_id !== cur.cbs_code_id
            }
          >
            {() => {
              const pid = form.getFieldValue('project_id');
              if (!pid) return null;
              return (
                <Form.Item label="Cost control (WBS / CBS)">
                  <CostControlLineFields
                    projectId={pid}
                    value={{
                      wbs_node_id: form.getFieldValue('wbs_node_id'),
                      cbs_code_id: form.getFieldValue('cbs_code_id'),
                      cost_category: form.getFieldValue('cost_category') || 'Overhead',
                    }}
                    onChange={(patch) => {
                      form.setFieldsValue({
                        wbs_node_id: patch.wbs_node_id,
                        cbs_code_id: patch.cbs_code_id,
                        cost_category: patch.cost_category || form.getFieldValue('cost_category'),
                      });
                    }}
                    size="middle"
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="quantity" label="Qty" initialValue={1}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="unit_cost" label="Unit cost">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="total_cost"
                label="Total cost"
                rules={[{ required: true, message: 'Required' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="currency" label="Currency" initialValue="AED">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <BudgetExceedanceRequestModal
        open={exceedanceOpen}
        onClose={() => setExceedanceOpen(false)}
        projectId={exceedanceProjectId}
        sourceEntityType="project_cost"
        sourceEntityId={exceedanceCostId}
        lines={exceedanceLines}
        checkResult={exceedanceCheck as any}
        onSubmitted={() => {
          queryClient.invalidateQueries({ queryKey: projectKeys.all });
        }}
      />
    </div>
  );
};
